<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  modelValue: {
    type: Array, // Array to store multiple selections
    default: () => [],
  },
  selectId: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: 'Sélectionner une option',
  },
  options: {
    type: Array,
    required: true,
  },
  size: {
    type: String,
    default: 'md',
    validator: (s) => ['md', 'lg'].includes(s),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: null,
  },
  success: {
    type: Boolean,
    default: null,
  },
  selectClass: {
    type: String,
    default: '',
  },
  required: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue']) // Define emit to allow emitting events

const selectedOptions = ref([...props.modelValue])

// Filter options to exclude those that are already selected
const filteredOptions = computed(() =>
  props.options.filter((option) => !selectedOptions.value.includes(option.key.toString()))
)

// Function to handle selecting an option
const addOption = (event) => {
  const selectedValue = event.target.value // integers are converted to string here
  if (selectedValue) {
    selectedOptions.value.unshift(selectedValue) // Add to the beginning of selected options
    emit('update:modelValue', selectedOptions.value)
  }
  event.target.value = '' // Reset select to placeholder
}

// Function to remove a selected option
const removeOption = (value) => {
  selectedOptions.value = selectedOptions.value.filter((option) => option !== value)
  emit('update:modelValue', selectedOptions.value)
}
</script>

<template>
  <div class="w-full relative">
    <!-- Select Dropdown -->
    <select
      :id="selectId"
      :required="required"
      :disabled="disabled"
      :class="[
        'border-none rounded-t initial:w-full h-full truncate',
        'bg-[#EEEEEE] focus:!shadow-[inset_0_-2px_0_0_#3A3A3A] text-[#3a3a3a]',
        { ' shadow-[inset_0_-2px_0_0_#3A3A3A]': !disabled },
        { '!shadow-[inset_0_-2px_0_0_#ce0500]': error },
        { '!shadow-[inset_0_-2px_0_0_#18753c]': success },
        { 'py-3': size === 'lg' },
        { 'cursor-not-allowed shadow-[inset_0_-2px_0_0_#E5E5E5]': disabled },
        selectClass,
      ]"
      @change="addOption"
    >
      <option value="" disabled selected>{{ placeholder }}</option>
      <option
        v-for="option in filteredOptions"
        :key="option.key"
        :value="option.key"
        :disabled="option.disabled"
      >
        {{ option.label }}
      </option>
    </select>

    <!-- Selected Options List -->
    <div v-if="selectedOptions.length" class="mt-2 flex flex-wrap gap-2">
      <DsfrTag
        v-for="selected in selectedOptions"
        :key="selected"
        :tag="selected"
        context="deletable"
        :size="size"
        @delete="removeOption(selected)"
      >
        {{ options.find((option) => option.key == selected)?.label }}
      </DsfrTag>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
select {
  --tw-ring-shadow: 0 0 #000 !important;
}
</style>
